import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  MapPinIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { exploreAPI, savedAPI } from '../services/api';
import ListMapView from '../components/ListMapView';
import ListPageContent from '../components/ListPageContent';
import MobileSearchOverlay from '../components/MobileSearchOverlay';
import BookmarkModal from '../components/BookmarkModal';
import EditProjectModal from '../components/EditProjectModal';
import ProjectCard from '../components/ProjectCard';
import SearchUI from '../components/SearchUI';
import { useAuth } from '../contexts/AuthContext';
import { useListMapView } from '../hooks/useListMapView';
import { useListPageData } from '../hooks/useListPageData';
import { useListPageFilters } from '../hooks/useListPageFilters';
import { canEditExploreProjects } from '../utils/permissions';
import { getImageUrl } from '../config/runtime';
import tagsData from '../data/tags.json';
import { getLocationFilterParams } from '../utils/locationFilters';
import { projectHasGeolocation } from '../utils/projectLocation';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'popularity', label: 'Most popular' }
];

const TemplatesPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  // Modal state
  const [bookmarkModal, setBookmarkModal] = useState({ isOpen: false, projectId: null, projectName: null });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isRequestingNearby, setIsRequestingNearby] = useState(false);

  // Combined filter tags
  const allFilterTags = useMemo(() => [
    { id: 'nearby', label: 'Nearby', icon: '📍', description: 'Find what\'s around you' },
    ...tagsData.tags
  ], []);
  const starterDomainTags = useMemo(
    () => allFilterTags.filter((tag) => tag.id !== 'nearby'),
    [allFilterTags]
  );

  // Use the reusable list/map view hook
  const listMapState = useListMapView({
    enableMap: true,
    treatTabletAsMobile: true,
    autoEnableMapFilter: true,
  });

  const {
    isMobile,
    viewMode,
    shouldRenderMobileMap,
    isDesktopSplitView,
    userLocation,
    isMobileSearchOpen,
    setIsMobileSearchOpen,
    mobileSearchHeaderRef,
    handleDesktopViewChange,
    isFullyExpanded,
  } = listMapState;

  // Use filter hook
  const filters = useListPageFilters({
    enableSearch: true,
    enableTags: true,
    enableSort: true,
    enableStatus: false,
    defaultSort: 'popularity',
    sortOptions: SORT_OPTIONS,
  });

  const {
    searchTerm,
    mobileSearchTerm,
    selectedTags,
    sortBy,
    mapViewportCenter,
    mapUserInteracted,
    mapSyncEnabled,
    setSearchTerm,
    setMobileSearchTerm,
    setSelectedTags,
    setSortBy,
    setMapViewportBbox,
    setMapViewportCenter,
    setMapViewportRadius,
    setMapUserInteracted,
    setMapSyncEnabled,
    handleSearch,
    clearAllFilters,
    filters: filterValues,
  } = filters;

  const mapAreaFilterActive = Boolean(
    mapViewportCenter && mapUserInteracted && mapSyncEnabled
  );

  const viewportStateHandlersRef = useRef({
    handleClearMapAreaFilter: null,
    handleEnableMapAreaFilter: null,
  });

  const handleViewportStateChange = useCallback((viewportState) => {
    const isMapVisible = shouldRenderMobileMap || (!isMobile && viewMode === 'map');

    viewportStateHandlersRef.current = {
      handleClearMapAreaFilter: viewportState.handleClearMapAreaFilter,
      handleEnableMapAreaFilter: viewportState.handleEnableMapAreaFilter,
    };
    
    setMapSyncEnabled(viewportState.mapSyncEnabled);
    setMapUserInteracted(viewportState.mapUserInteracted);

    if (isMapVisible && viewportState.mapViewportCenter) {
      setMapViewportBbox(viewportState.mapViewportBbox);
      setMapViewportCenter(viewportState.mapViewportCenter);
      setMapViewportRadius(viewportState.mapViewportRadius);
    }
  }, [shouldRenderMobileMap, isMobile, viewMode, setMapViewportBbox, setMapViewportCenter, setMapViewportRadius, setMapUserInteracted, setMapSyncEnabled]);

  const handleClearMapAreaFilter = useCallback(() => {
    viewportStateHandlersRef.current.handleClearMapAreaFilter?.();
  }, []);

  const isNearbyActive = selectedTags.includes('nearby');

  // Navigation handler
  const navigateToProject = useCallback((projectId, source = 'unknown') => {
    console.log(`🚀 Navigation triggered from: ${source} to project: ${projectId}`);
    navigate(`/starters/${projectId}`, {
      state: {
        previousPath: '/starters',
        transitionDirection: 'forward',
      },
    });
  }, [navigate]);

  // Request location for nearby filter
  useEffect(() => {
    let cancelled = false;

    const fetchNearbyLocation = async () => {
      if (!isNearbyActive || userLocation || isRequestingNearby) return;

      setIsRequestingNearby(true);
      try {
        const { LocationService } = await import('../services/LocationService');
        await LocationService.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        });
      } catch (error) {
        if (!cancelled) {
          toast.error('Turn on location to see nearby starters');
          setSelectedTags((prev) => prev.filter((tag) => tag !== 'nearby'));
        }
      } finally {
        if (!cancelled) {
          setIsRequestingNearby(false);
        }
      }
    };

    fetchNearbyLocation();

    return () => {
      cancelled = true;
    };
  }, [isNearbyActive, userLocation, isRequestingNearby, setSelectedTags]);

  // Build query parameters
  const buildQueryParams = useCallback((filters, pageParam, limit) => {
    const params = {
      offset: pageParam,
      limit,
    };
    
    const allTags = (filters.selectedTags || []).filter(tag => tag !== 'nearby');
    
    if (filters.searchTerm) params.search = filters.searchTerm;
    if (allTags.length > 0) params.tags = allTags.join(',');
    if (filters.sortBy) params.sort = filters.sortBy;
    
    const locationParams = getLocationFilterParams(filters, userLocation);

    if (locationParams) {
      params.lat = locationParams.lat;
      params.lng = locationParams.lng;
      params.radius = locationParams.radius;
    }

    return params;
  }, [userLocation]);

  // Transform response
  const transformResponse = useCallback((response) => {
    return response?.data?.data?.projects || [];
  }, []);
  
  // Use data hook
  const { 
    projects,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
  } = useListPageData({
    queryKey: 'explore-projects',
    queryFn: (params) => exploreAPI.getAll(params),
    buildParams: buildQueryParams,
    transformResponse,
    filters: filterValues,
    limit: 20,
    maxPages: 10,
    staleTime: 5 * 60 * 1000,
  });

  const isMapFilteringActive = mapAreaFilterActive;

  const projectsForDisplay = useMemo(() => {
    if (!mapAreaFilterActive) {
      return projects;
    }
    return projects.filter(projectHasGeolocation);
  }, [projects, mapAreaFilterActive]);

  // Get project saved status (bulk request)
  const projectIds = useMemo(() => {
    const ids = projectsForDisplay.map(p => p.id);
    return [...ids].sort();
  }, [projectsForDisplay]);
  
  const { data: savedStatusResponse } = useQuery({
    queryKey: ['project-saved-status-bulk', projectIds.length, projectIds.join(',')],
    queryFn: async () => {
      if (!user || projectIds.length === 0) return {};
      
        try {
        const response = await savedAPI.getProjectsStatusBulk(projectIds);
        return response.data.data || {};
        } catch (error) {
        console.error('Failed to fetch bulk saved status:', error);
        return projectIds.reduce((acc, projectId) => {
          acc[projectId] = { isSaved: false, savedInLists: [] };
          return acc;
        }, {});
      }
    },
    enabled: !!user && projectIds.length > 0,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  // Search handlers
  const applyMobileSearch = useCallback(() => {
    handleSearch();
    setIsMobileSearchOpen(false);
  }, [handleSearch, setIsMobileSearchOpen]);

  const handleNearbyToggle = useCallback(() => {
    setSelectedTags((prev) => {
      if (prev.includes('nearby')) {
        setIsRequestingNearby(false);
        return prev.filter((tag) => tag !== 'nearby');
      }
      return [...prev, 'nearby'];
    });
  }, [setSelectedTags]);

  const handleTagToggle = useCallback((tagId) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter(t => t !== tagId));
    } else {
      setSelectedTags([tagId]);
    }
  }, [selectedTags, setSelectedTags]);

  // Modal handlers
  const handleCreateProject = useCallback(() => {
    if (!canEditExploreProjects(user)) {
      toast.error('You do not have permission to create explore projects');
      return;
    }
    setShowCreateModal(true);
  }, [user]);

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  const handleCreateSuccess = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  const handleBookmarkClick = useCallback((e, project) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error('Please login to save projects');
      return;
    }
    
    setBookmarkModal({ 
      isOpen: true, 
      projectId: project.id, 
      projectName: project.name 
    });
  }, [user]);

  const closeBookmarkModal = useCallback(() => {
    setBookmarkModal({ isOpen: false, projectId: null, projectName: null });
  }, []);

  // Filter summary for mobile search
  const selectedTagLabels = selectedTags
    .filter((tag) => tag !== 'nearby')
    .map((tag) => tagsData.tags.find((t) => t.id === tag)?.label)
    .filter(Boolean);
  const sortLabel = SORT_OPTIONS.find((option) => option.value === sortBy)?.label;
  const filterSummaryParts = [];
  if (isNearbyActive) filterSummaryParts.push('Nearby');
  if (selectedTagLabels.length > 0) {
    const preview = selectedTagLabels.slice(0, 2).join(', ');
    const extra = selectedTagLabels.length > 2 ? ` +${selectedTagLabels.length - 2}` : '';
    filterSummaryParts.push(`${preview}${extra}`);
  }
  if (sortLabel) {
    filterSummaryParts.push(sortLabel);
  }
  const mobileFilterSummary = filterSummaryParts.length > 0 ? filterSummaryParts.join(' · ') : 'Any filters';
  const mobileSearchSummary = (() => {
    if (searchTerm?.trim()) return searchTerm;
    if (mapAreaFilterActive) return 'Results in map area';
    return 'All Starters';
  })();

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async (invalidateKeys) => {
    await queryClient.invalidateQueries({ queryKey: ['explore-projects'] });
    await queryClient.invalidateQueries({ queryKey: ['project-saved-status-bulk'] });
    await refetch(invalidateKeys);
  }, [queryClient, refetch]);

  // Render project card
  const renderProject = useCallback((project, index) => {
    return (
      <ProjectCard 
        project={project} 
        showBookmark={true}
        savedStatus={savedStatusResponse?.[project.id] || { isSaved: false, savedInLists: [] }}
        onBookmarkClick={handleBookmarkClick}
        onProjectClick={(projectId) => navigateToProject(projectId, 'mobile-card')}
        isMobile={isMobile}
      />
    );
  }, [savedStatusResponse, handleBookmarkClick, navigateToProject, isMobile]);

  // Render header
  const renderHeader = useCallback(() => {
    return (
      <div className="flex flex-col gap-4">
        <div className={`flex ${isMobile ? 'flex-col gap-2' : 'justify-between gap-4'}`}>
          <div>
            {!isMobile && (
              <div className="flex flex-col gap-1">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Project Starters</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">Ready-made guides to begin your next project</p>
              </div>
            )}
            {isMobile && isFullyExpanded && (
              <div className="flex flex-col gap-1">
                <div className="flex justify-between items-center gap-2">
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">Project Starters</h1>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Ready-made guides to begin your next project</p>
              </div>
            )}
          </div>
          <div className={`flex ${isMobile ? 'flex-col gap-3 w-full' : 'items-center gap-3 flex-wrap justify-end'}`}>
            {!isMobile && (
              <>
                <div className="flex-1 max-w-md">
                  <SearchUI
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    selectedTags={selectedTags}
                    setSelectedTags={setSelectedTags}
                    tags={allFilterTags}
                    searchPlaceholder="Search"
                    onSearch={handleSearch}
                    onClearAll={clearAllFilters}
                    mapAreaFilterActive={mapAreaFilterActive}
                    onClearMapAreaFilter={handleClearMapAreaFilter}
                    className="w-full"
                  />
                </div>
                <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1 text-sm font-medium">
                  <button
                    onClick={() => handleDesktopViewChange('list')}
                    className={`px-4 py-1.5 rounded-full transition-all flex items-center gap-1.5 ${
                      viewMode === 'list'
                        ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    <Bars3Icon className="w-4 h-4" />
                    List
                  </button>
                  <button
                    onClick={() => handleDesktopViewChange('map')}
                    className={`px-4 py-1.5 rounded-full transition-all flex items-center gap-1.5 ${
                      viewMode === 'map'
                        ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    <MapPinIcon className="w-4 h-4" />
                    Map
                  </button>
                </div>
                <div className="inline-flex items-center gap-2">
                  <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1">
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setSortBy(option.value)}
                        aria-label={`Sort by ${option.label}`}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                          sortBy === option.value
                            ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            {canEditExploreProjects(user) && !isMobile && (
              <button
                onClick={handleCreateProject}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-500 text-white px-5 py-1.5 font-semibold shadow-lg hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-300 transition"
              >
                <PlusIcon className="w-4 h-4" />
                <span className="hidden sm:inline">New Starter</span>
              </button>
            )}
          </div>
        </div>
        {!isMobile && (
          <div className="flex flex-wrap items-center gap-2">
            {starterDomainTags.map((tag) => {
              const isSelected = selectedTags.includes(tag.id);

              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => handleTagToggle(tag.id)}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                    isSelected
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:border-emerald-400 dark:bg-emerald-500/15 dark:text-emerald-200'
                      : 'border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
                  }`}
                  aria-pressed={isSelected}
                  aria-label={`Filter starters by ${tag.label}`}
                >
                  <span aria-hidden="true">{tag.icon}</span>
                  <span>{tag.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }, [
    isMobile,
    isFullyExpanded,
    searchTerm,
    selectedTags,
    sortBy,
    viewMode,
    handleSearch,
    clearAllFilters,
    setSearchTerm,
    setSelectedTags,
    setSortBy,
    handleDesktopViewChange,
    user,
    handleCreateProject,
    allFilterTags,
    starterDomainTags,
    handleTagToggle,
    mapAreaFilterActive,
    handleClearMapAreaFilter,
  ]);

  // Mobile search overlay using unified component
  const mobileSearchOverlay = (
    <MobileSearchOverlay
      isOpen={isMobile && isMobileSearchOpen}
      onClose={() => setIsMobileSearchOpen(false)}
      title="Search Starters"
      searchPlaceholder="Search Starters"
      mobileSearchTerm={mobileSearchTerm}
      onSearchTermChange={setMobileSearchTerm}
      onSearch={applyMobileSearch}
      selectedTags={selectedTags}
      onTagToggle={handleTagToggle}
      allTags={allFilterTags}
      sortBy={sortBy}
      sortOptions={SORT_OPTIONS}
      onSortChange={setSortBy}
      showSort={true}
      showTags={true}
      mapAreaFilterActive={mapAreaFilterActive}
      onClearMapAreaFilter={handleClearMapAreaFilter}
      onClearAll={clearAllFilters}
      onNearbyToggle={handleNearbyToggle}
    />
  );

  // Render list content using ListPageContent component
  const listContent = (
    <ListPageContent
      projects={projectsForDisplay}
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      refetch={refetch}
      error={error}
      renderProject={renderProject}
      isMobile={isMobile}
      isDesktopSplitView={isDesktopSplitView}
      isFullyExpanded={isFullyExpanded}
      onRefresh={handleRefresh}
      invalidateKeys={[['explore-projects'], ['project-saved-status-bulk']]}
      emptyState={{
        title: 'No results found',
        message: 'Try adjusting your search or filters',
        icon: MapPinIcon,
      }}
      header={renderHeader()}
    />
  );

  // Render map preview card
  const renderMapPreview = useCallback((project, { onClose }) => {
    return (
      <div 
        className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
        onClick={() => {
          navigateToProject(project.id, 'map-preview-card');
          onClose();
        }}
      >
        <div className="relative">
          {project.images && project.images.length > 0 ? (
            <img
              src={getImageUrl(project.images[0], 'cardmobile')}
              alt={project.name}
              className="w-full h-44 object-cover"
            />
          ) : (
            <div className="w-full h-44 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <MapPinIcon className="w-12 h-12 text-gray-400" />
            </div>
          )}
          
          <div className="absolute top-3 right-3 flex gap-2">
            {user && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleBookmarkClick(e, project);
                }}
                className="w-8 h-8 bg-white/90 dark:bg-gray-800/90 rounded-full flex items-center justify-center shadow-md backdrop-blur-sm"
                aria-label="Save"
              >
                <svg className="w-4 h-4 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
            )}
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="w-8 h-8 bg-white/90 dark:bg-gray-800/90 rounded-full flex items-center justify-center shadow-md backdrop-blur-sm"
              aria-label="Close"
            >
              <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-gray-200" />
            </button>
          </div>
        </div>
        
        <div className="p-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-gray-900 dark:text-white text-base leading-tight">
              {project.name}
            </h3>
            {project.rating?.average > 0 && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className="text-sm">★</span>
                <span className="text-sm font-medium">{project.rating.average.toFixed(1)}</span>
              </div>
            )}
          </div>
          
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
            {project.description || (project.difficulty && 
              `${project.difficulty.charAt(0).toUpperCase() + project.difficulty.slice(1)} difficulty`
            )}
          </p>
          
          {project.distance && (
            <p className="text-sm text-gray-900 dark:text-white font-medium mt-2">
              {project.distance < 1 
                ? `${Math.round(project.distance * 1000)}m away`
                : `${project.distance.toFixed(1)}km away`
              }
            </p>
          )}
        </div>
      </div>
    );
  }, [navigateToProject, user, handleBookmarkClick]);

  // Render floating action button
  const renderFloatingActions = useCallback(() => {
    if (!canEditExploreProjects(user)) return null;
    return (
      <button
        onClick={handleCreateProject}
        className={`${isMobile ? 'w-16 h-16' : 'w-14 h-14'} bg-emerald-500 dark:bg-emerald-500 text-white rounded-full shadow-2xl flex items-center justify-center active:scale-95 transition-transform hover:bg-emerald-600`}
        style={{
          boxShadow: '0 10px 25px -5px rgba(6, 182, 212, 0.4), 0 8px 10px -6px rgba(6, 182, 212, 0.3)',
        }}
        aria-label="New Starter"
      >
        <PlusIcon className={isMobile ? 'w-7 h-7' : 'w-6 h-6'} />
      </button>
    );
  }, [user, handleCreateProject, isMobile]);

  return (
    <>
      {isMobile && !isMobileSearchOpen && (
        <div
          ref={mobileSearchHeaderRef}
          className="fixed pb-3 px-5 top-0 left-0 right-0 z-[700] pointer-events-auto bg-white dark:bg-gray-900"
          style={{ paddingTop: 'var(--app-safe-area-top)' }}
        >
          <button
            onClick={() => setIsMobileSearchOpen(true)}
            className="mt-3 w-full bg-white/95 dark:bg-gray-700 border border-gray-200 dark:border-gray-600/50 rounded-full px-4 h-[56px] shadow-lg flex items-center gap-3"
          >
            {!searchTerm?.trim() && selectedTags.length === 0 && sortBy === 'popularity' && !isMapFilteringActive ? (
              <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
                <div className="flex items-center justify-center gap-2">
                  <MagnifyingGlassIcon className="w-4 h-4 text-black dark:text-gray-300" />
                  Search Starters
                </div>
              </span>
            ) : (
              <div className="flex-1 text-center">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{mobileSearchSummary}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {isMapFilteringActive
                    ? (mobileFilterSummary !== 'Any filters' ? `Map area · ${mobileFilterSummary}` : 'Filtered by map area')
                    : mobileFilterSummary}
                </p>
              </div>
            )}
          </button>
          <div className="mt-3 -mx-1 px-1 overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-2 min-w-max">
              {starterDomainTags.map((tag) => {
                const isSelected = selectedTags.includes(tag.id);

                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleTagToggle(tag.id)}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                      isSelected
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:border-emerald-400 dark:bg-emerald-500/15 dark:text-emerald-200'
                        : 'border-gray-200 bg-white/95 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
                    }`}
                    aria-pressed={isSelected}
                    aria-label={`Filter starters by ${tag.label}`}
                  >
                    <span aria-hidden="true">{tag.icon}</span>
                    <span>{tag.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <ListMapView
          listContent={listContent}
          projects={projectsForDisplay}
          onProjectSelect={(projectId) => navigateToProject(projectId, 'mobile-map')}
          onMarkerSelect={(project) => {
            // Selected item is managed by ListMapView internally
          }}
          renderMapPreview={renderMapPreview}
          renderFloatingActions={renderFloatingActions}
          mapConfig={{
            mode: 'view',
            layoutContext: 'explore',
            autoCenterOnUser: true,
          }}
          hookConfig={{
            enableMap: true,
            treatTabletAsMobile: true,
          }}
          hookState={listMapState}
          onViewportStateChange={handleViewportStateChange}
          emptyStateTitle="No results found"
          emptyStateMessage="Try adjusting your search or filters"
        />

      {/* Bookmark Modal */}
      <BookmarkModal
        isOpen={bookmarkModal.isOpen}
        onClose={closeBookmarkModal}
        projectId={bookmarkModal.projectId}
        projectName={bookmarkModal.projectName}
      />

      {/* Create Project Modal */}
      {showCreateModal && (
        <EditProjectModal
          key="create-project-modal-stable"
          isOpen={true} 
          onClose={handleCloseCreateModal}
          project={null}
          mode="create"
          onSuccess={handleCreateSuccess}
        />
      )}
      
      {mobileSearchOverlay}
    </>
  );
};

export default TemplatesPage;
